import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../api';
import { useToast } from '../contexts/ToastContext';
import { resolveImageUrl } from '../utils/imageUrl';
import './Directory.css';

const EMPTY_DIRECTORY = {
  heroBillboard: null,
  listings: [],
  categories: [],
  locations: [],
  meta: { total: 0 },
};

const normalizeDirectory = (feed = {}) => {
  const legacyListings = [
    feed.heroBillboard,
    ...(feed.featuredListings || []),
    ...(feed.categoryTopAds || []),
    ...(feed.sponsoredCards || []),
    ...(feed.contentAds || []),
    ...(feed.sidebarAds || []),
  ].filter(Boolean);

  return {
    ...EMPTY_DIRECTORY,
    ...feed,
    listings: Array.isArray(feed.listings) ? feed.listings : legacyListings,
    categories: Array.isArray(feed.categories) ? feed.categories : [],
    locations: Array.isArray(feed.locations) ? feed.locations : [],
  };
};

const eventUrl = (listing) => `${API_URL}/directory/${listing.source || 'advertisement'}/${listing.id}/events`;

function useDirectoryFeed(filters, retryKey) {
  const [state, setState] = useState({ data: EMPTY_DIRECTORY, loading: true, error: null });

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        setState((current) => ({ ...current, loading: true, error: null }));
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
        const suffix = params.toString() ? `?${params}` : '';
        const feedResponse = await fetch(`${API_URL}/ads/directory${suffix}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!feedResponse.ok) throw new Error(`Directory request failed: ${feedResponse.status}`);
        const feed = await feedResponse.json();
        setState({ data: normalizeDirectory(feed), loading: false, error: null });
      } catch (error) {
        if (error.name !== 'AbortError') {
          setState((current) => ({ ...current, loading: false, error }));
        }
      }
    }, filters.q ? 280 : 0);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [filters, retryKey]);

  return state;
}

export default function Directory() {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [source, setSource] = useState('');
  const [sort, setSort] = useState('recommended');
  const [retryKey, setRetryKey] = useState(0);
  const filters = useMemo(() => ({ q: query.trim(), category, location, source, sort }), [query, category, location, source, sort]);
  const { data, loading, error } = useDirectoryFeed(filters, retryKey);

  const track = useCallback(async (listing, eventType) => {
    try {
      const response = await fetch(eventUrl(listing), {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          event_type: eventType,
          campaign_id: listing.campaignId || null,
          placement: listing.placement || null,
        }),
      });
      if (!response.ok) throw new Error(`Tracking failed: ${response.status}`);
    } catch (trackingError) {
      if (eventType !== 'impression') showError('Your link will still open, but we could not record this visit.');
    }
  }, [showError]);

  const openListing = useCallback((listing) => {
    const destination = listing.link;
    if (!destination) {
      showError('This partner has not added contact information yet.');
      return;
    }
    const isContact = destination.startsWith('tel:') || destination.startsWith('mailto:');
    track(listing, isContact ? 'contact' : 'click');
    if (destination.startsWith('/')) {
      navigate(destination);
    } else if (isContact) {
      window.location.href = destination;
    } else {
      window.open(destination, '_blank', 'noopener,noreferrer');
    }
  }, [navigate, showError, track]);

  const clearFilters = () => {
    setQuery(''); setCategory(''); setLocation(''); setSource(''); setSort('recommended');
  };
  const hasFilters = Boolean(query || category || location || source || sort !== 'recommended');
  const listings = data.listings.filter((listing) => listing.isActive !== false);
  const hero = data.heroBillboard?.isActive !== false ? data.heroBillboard : null;
  const listingKey = (item) => item ? (item.sourceKey || `${item.source || 'advertisement'}:${item.id}`) : null;
  const heroKey = listingKey(hero);
  const featured = listings.filter((item) => ['featured_listing', 'category_top'].includes(item.placement) && listingKey(item) !== heroKey);
  const featuredKeys = new Set(featured.map(listingKey));
  const results = listings.filter((item) => listingKey(item) !== heroKey && !featuredKeys.has(listingKey(item)));

  return (
    <main className="directory-page">
      <section className="directory-intro" aria-labelledby="directory-title">
        <div className="directory-intro__glow" aria-hidden="true" />
        <div className="directory-shell directory-intro__content">
          <p className="directory-kicker">Guhso community directory</p>
          <h1 id="directory-title">Find good people doing <span>great work.</span></h1>
          <p className="directory-lede">Discover local businesses, independent brands, useful services, and Guhso products. Search by what you need or where you are.</p>

          <form className="directory-search" role="search" onSubmit={(event) => event.preventDefault()}>
            <label className="directory-search__field">
              <span className="visually-hidden">Search the Directory</span>
              <i className="fas fa-search" aria-hidden="true" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Try “catering”, “tech repair”, or “Kingston”" />
            </label>
            <label>
              <span className="visually-hidden">Category</span>
              <select value={category} onChange={(event) => setCategory(event.target.value)}>
                <option value="">All categories</option>
                {data.categories.map((item) => <option key={item.id} value={item.slug}>{item.name}</option>)}
              </select>
            </label>
            <label>
              <span className="visually-hidden">Location</span>
              <select value={location} onChange={(event) => setLocation(event.target.value)}>
                <option value="">Everywhere</option>
                {data.locations.map((item) => <option key={item.id} value={item.slug}>{item.name}</option>)}
              </select>
            </label>
            <button type="button" className="directory-search__submit" aria-label="Search"><i className="fas fa-arrow-right" /></button>
          </form>

          {data.categories.length > 0 && (
            <div className="directory-category-chips" aria-label="Popular categories">
              <span>Explore:</span>
              {data.categories.slice(0, 6).map((item) => (
                <button key={item.id} type="button" className={category === item.slug ? 'is-active' : ''} onClick={() => setCategory(category === item.slug ? '' : item.slug)}>
                  {item.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="directory-shell directory-body">
        <div className="directory-toolbar">
          <div>
            <p className="directory-result-count">{loading ? 'Searching…' : `${data.meta?.total ?? listings.length} ${listings.length === 1 ? 'result' : 'results'}`}</p>
            <h2>{hasFilters ? 'Matches for you' : 'Community picks & paid partners'}</h2>
          </div>
          <div className="directory-toolbar__controls">
            <label>Type<select value={source} onChange={(event) => setSource(event.target.value)}><option value="">All listings</option><option value="advertisement">Businesses</option><option value="product">Guhso products</option></select></label>
            <label>Sort<select value={sort} onChange={(event) => setSort(event.target.value)}><option value="recommended">Recommended</option><option value="name">Name A–Z</option><option value="newest">Newest</option></select></label>
            {hasFilters && <button type="button" className="directory-clear" onClick={clearFilters}>Clear filters</button>}
          </div>
        </div>

        {loading && <DirectorySkeleton />}
        {!loading && error && (
          <section className="directory-state" role="alert"><i className="fas fa-satellite-dish" /><h2>We lost the Directory signal.</h2><p>Your filters are safe. Try reconnecting.</p><button type="button" onClick={() => setRetryKey((value) => value + 1)}>Try again</button></section>
        )}

        {!loading && !error && listings.length === 0 && (
          <section className="directory-state"><i className="fas fa-compass" /><h2>{hasFilters ? 'No exact matches yet.' : 'The Directory is opening soon.'}</h2><p>{hasFilters ? 'Try a broader search, another location, or clear your filters.' : 'Be among the first businesses featured in the Guhso community.'}</p><div><button type="button" onClick={hasFilters ? clearFilters : () => navigate('/advertise')}>{hasFilters ? 'Clear filters' : 'Advertise with Guhso'}</button></div></section>
        )}

        {!loading && !error && listings.length > 0 && (
          <>
            {hero && <HeroListing listing={hero} onOpen={openListing} onImpression={track} />}
            {featured.length > 0 && <section className="directory-featured" aria-labelledby="featured-directory-title"><div className="directory-section-heading"><div><p>Priority placement</p><h2 id="featured-directory-title">Featured partners</h2></div><span>Paid placements are clearly marked</span></div><div className="directory-featured-grid">{featured.map((listing) => <ListingCard key={listing.sourceKey || `${listing.source}:${listing.id}`} listing={listing} featured onOpen={openListing} onImpression={track} />)}</div></section>}
            {results.length > 0 && <section className="directory-results" aria-labelledby="all-directory-title"><div className="directory-section-heading"><div><p>Browse the community</p><h2 id="all-directory-title">More to discover</h2></div></div><div className="directory-card-grid">{results.map((listing) => <ListingCard key={listing.sourceKey || `${listing.source}:${listing.id}`} listing={listing} onOpen={openListing} onImpression={track} />)}</div></section>}
          </>
        )}
      </div>

      <aside className="directory-partner-cta" aria-labelledby="directory-advertise-title">
        <div className="directory-shell">
          <span className="directory-partner-cta__icon" aria-hidden="true"><i className="fas fa-bullhorn" /></span>
          <div>
            <p className="directory-kicker">Advertise with us</p>
            <h2 id="directory-advertise-title">Want your business featured here?</h2>
            <p>View live placements, clear pricing, and available campaign dates.</p>
          </div>
          <Link to="/advertise">Explore advertising <i className="fas fa-arrow-right" /></Link>
        </div>
      </aside>
    </main>
  );
}

function ImpressionBoundary({ listing, onImpression, children, className, onClick }) {
  const ref = useRef(null);
  const recorded = useRef(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || recorded.current || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && !recorded.current) {
        recorded.current = true;
        onImpression(listing, 'impression');
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [listing, onImpression]);
  return <article ref={ref} className={className} onClick={onClick}>{children}</article>;
}

function HeroListing({ listing, onOpen, onImpression }) {
  return <ImpressionBoundary listing={listing} onImpression={onImpression} className="directory-hero-listing" onClick={() => onOpen(listing)}>
    {listing.imageUrl && <img src={resolveImageUrl(listing.imageUrl)} alt="" />}
    <div className="directory-hero-listing__shade" />
    <div className="directory-hero-listing__content"><PaidBadge label="Spotlight partner" /><p className="directory-card-meta">{listing.category}{listing.location ? ` · ${listing.location}` : ''}</p><h2>{listing.title}</h2><p>{listing.description}</p><button type="button">{listing.cta_text || 'Learn more'} <i className="fas fa-arrow-right" /></button></div>
  </ImpressionBoundary>;
}

function ListingCard({ listing, featured = false, onOpen, onImpression }) {
  return <ImpressionBoundary listing={listing} onImpression={onImpression} className={`directory-card${featured ? ' directory-card--featured' : ''}`} onClick={() => onOpen(listing)}>
    <div className="directory-card__media">{listing.imageUrl ? <img src={resolveImageUrl(listing.imageUrl)} alt="" loading="lazy" /> : <div className="directory-card__placeholder"><span>{listing.title?.slice(0, 1)}</span></div>}<PaidBadge label={featured ? 'Featured' : listing.source === 'product' ? 'Guhso product' : 'Sponsored'} /></div>
    <div className="directory-card__body"><p className="directory-card-meta">{listing.category || 'Partner'}{listing.location ? ` · ${listing.location}` : ''}</p><h3>{listing.title}</h3><p>{listing.description}</p><div className="directory-card__footer"><span>{listing.cta_text || (listing.phone ? 'Call now' : listing.email ? 'Email' : 'Explore')}</span><i className="fas fa-arrow-right" /></div></div>
  </ImpressionBoundary>;
}

function PaidBadge({ label }) {
  return <span className="directory-paid-badge"><i className="fas fa-bolt" aria-hidden="true" /> {label}</span>;
}


function DirectorySkeleton() {
  return <div className="directory-skeleton" aria-label="Loading Directory"><div className="directory-skeleton__hero" /><div className="directory-card-grid">{[1, 2, 3, 4, 5, 6].map((item) => <div key={item} className="directory-skeleton__card" />)}</div></div>;
}
